import { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';

// material-ui
import { useTheme } from '@mui/material/styles';
import {
  Avatar,
  Box,
  Button,
  ButtonBase,
  CardActions,
  Chip,
  ClickAwayListener,
  Divider,
  Grid,
  Paper,
  Popper,
  Stack,
  TextField,
  Typography,
  useMediaQuery
} from '@mui/material';

// third-party
import PerfectScrollbar from 'react-perfect-scrollbar';

// project imports
import MainCard from 'ui-component/cards/MainCard';
import Transitions from 'ui-component/extended/Transitions';
import NotificationList from './NotificationList';
import {
  useGetNotificationListQuery,
  useMarkAsReadMutation,
  useMarkAllAsReadMutation
} from '../../../../services/notification/notification';
// assets
// import { IconBell } from '@tabler/icons';
import NotificationsIcon from '@mui/icons-material/Notifications';
import { toast } from 'react-toastify';
// notification status options
const status = [
  {
    value: 'all',
    label: 'All Notification'
  },
  {
    value: 'new',
    label: 'New'
  },
  {
    value: 'unread',
    label: 'Unread'
  },
  {
    value: 'other',
    label: 'Other'
  }
];

// ==============================|| NOTIFICATION ||============================== //

const NotificationSection = () => {
  const theme = useTheme();
  const matchesXs = useMediaQuery(theme.breakpoints.down('md'));
  // Poll: the bell used to fetch only on page load, so anything arriving
  // while you sat on a page (a client saving metrics, uploading progress
  // photos) stayed invisible until a manual refresh.
  const { data: notificationsListing, isLoading } = useGetNotificationListQuery(
    { limit: 100, offset: 0 },
    { pollingInterval: 15000, refetchOnFocus: true }
  );
  const [markAsRead] = useMarkAsReadMutation();
  const [markAllAsRead] = useMarkAllAsReadMutation();
  const [open, setOpen] = useState(false);
  const [value, setValue] = useState('');
  /**
   * anchorRef is used on different componets and specifying one type leads to other components throwing an error
   * */
  const anchorRef = useRef(null);

  const handleToggle = () => {
    setOpen((prevOpen) => {
      // Message-style: OPENING the bell counts as reading — clear MY unread
      // (each staff member's copies are their own, so this never touches a
      // coach's dot).
      if (!prevOpen && notificationsListing?.myUnread > 0) {
        markAllAsRead()
          .unwrap()
          .catch(() => undefined);
      }
      return !prevOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current && anchorRef.current.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const prevOpen = useRef(open);
  useEffect(() => {
    if (prevOpen.current === true && open === false) {
      anchorRef.current.focus();
    }
    prevOpen.current = open;
  }, [open]);
  const handleChange = (event) => {
    if (event?.target.value) setValue(event?.target.value);
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead().unwrap();
      toast.success('All notificatins are read successfully.');
    } catch (error) {
      if (Array.isArray(error?.data?.message)) {
        error?.data?.message?.forEach((msg) => {
          toast.error(msg);
        });
      } else {
        toast.error(error?.data?.message);
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          ml: 2,
          mr: 1,
          [theme.breakpoints.down('md')]: {
            mr: 1
          }
        }}
      >
        <ButtonBase sx={{ borderRadius: '12px' }}>
          <Avatar
            variant="rounded"
            sx={{
              position: 'relative',
              // ...theme.typography.commonAvatar,
              // ...theme.typography.mediumAvatar,
              transition: 'all .2s ease-in-out',
              borderColor: '#3c3847',
              backgroundColor: '#3c3847',
              '&[aria-controls="menu-list-grow"], &:hover': {
                borderColor: '#292632',
                background: `${theme.palette.primary.main}!important`,
                color: theme.palette.primary.light,
                '& svg': {
                  stroke: '#3c3847'
                }
              }
            }}
            ref={anchorRef}
            aria-controls={open ? 'menu-list-grow' : undefined}
            aria-haspopup="true"
            onClick={handleToggle}
            color="inherit"
          >
            {/* Dot = MY unread only. Counting the all-seeing view's totals kept
                it lit forever (other people's copies can't be marked read). */}
            {notificationsListing?.myUnread > 0 && (
              <Box
                sx={{
                  height: '10px',
                  width: '10px',
                  borderRadius: '50%',
                  backgroundColor: '#FF4D6D',
                  position: 'absolute',
                  top: 2,
                  right: 2
                }}
              />
            )}
            <NotificationsIcon stroke={1.5} size="1.4rem" htmlColor='#ffffff' />
          </Avatar>
        </ButtonBase>
      </Box>
      <Popper
        placement={matchesXs ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [matchesXs ? 5 : 0, 20]
              }
            }
          ]
        }}
      >
        {({ TransitionProps }) => (
          <Transitions position={matchesXs ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard border={false} elevation={16} content={false} boxShadow shadow={theme.shadows[16]}>
                  <Grid container direction="column" spacing={2}>
                    <Grid item xs={12}>
                      <Grid container alignItems="center" justifyContent="space-between" sx={{ pt: 2, px: 2 }}>
                        <Grid item>
                          <Stack direction="row" spacing={2}>
                            <Typography variant="subtitle1">All Notification</Typography>
                            {notificationsListing?.myUnread > 0 && (
                              <Chip
                                size="small"
                                label={notificationsListing?.myUnread}
                                sx={{
                                  color: theme.palette.background.default,
                                  bgcolor: '#ed701b'
                                }}
                              />
                            )}
                          </Stack>
                        </Grid>
                        <Grid item>
                          <Typography
                            component={Link}
                            onClick={handleMarkAllAsRead}
                            to="#"
                            variant="subtitle2"
                            color="primary"
                            sx={{ ml: 2 }}
                          >
                            Mark as all read
                          </Typography>
                        </Grid>
                      </Grid>
                    </Grid>
                    <Grid item xs={12}>
                      <PerfectScrollbar style={{ height: '100%', maxHeight: 'calc(100vh - 205px)', overflowX: 'hidden' }}>
                        <Grid container direction="column" spacing={2}>
                          {/*<Grid item xs={12}>*/}
                          {/*  <Box sx={{ px: 2, pt: 0.25 }}>*/}
                          {/*    <TextField*/}
                          {/*      id="outlined-select-currency-native"*/}
                          {/*      select*/}
                          {/*      fullWidth*/}
                          {/*      value={value}*/}
                          {/*      onChange={handleChange}*/}
                          {/*      SelectProps={{*/}
                          {/*        native: true*/}
                          {/*      }}*/}
                          {/*    >*/}
                          {/*      {status.map((option) => (*/}
                          {/*        <option key={option.value} value={option.value}>*/}
                          {/*          {option.label}*/}
                          {/*        </option>*/}
                          {/*      ))}*/}
                          {/*    </TextField>*/}
                          {/*  </Box>*/}
                          {/*</Grid>*/}
                          <Grid item xs={12} p={0}>
                            <Divider sx={{ my: 0 }} />
                          </Grid>
                        </Grid>
                        <NotificationList data={notificationsListing?.data} loading={isLoading} />
                      </PerfectScrollbar>
                    </Grid>
                  </Grid>
                  {/*<Divider />*/}
                  {/*<CardActions sx={{ p: 1.25, justifyContent: 'center' }}>*/}
                  {/*  <Button size="small" disableElevation>*/}
                  {/*    View All*/}
                  {/*  </Button>*/}
                  {/*</CardActions>*/}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </>
  );
};

export default NotificationSection;
